import { useMediaQuery } from "@material-ui/core";
import { MenuItemProps } from "@material-ui/core/MenuItem";
import { makeStyles, Theme } from "@material-ui/core/styles";
import LockIcon from "@material-ui/icons/Lock";
import ExitIcon from "@material-ui/icons/PowerSettingsNew";
import classnames from "classnames";
import PropTypes from "prop-types";
import * as React from "react";
import { FunctionComponent, ReactElement, useCallback } from "react";
import {
  MenuItemLink,
  useLogout,
  useNotify,
  useRedirect,
  useTranslate,
} from "react-admin";
import useOCPAuthProvider from "../../shared/useOCPAuthProvider";
import useUserContext from "../../shared/useUserContext";

interface Props {
  className?: string;
  redirectTo?: string;
  icon?: ReactElement;
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    menuItem: {
      color: theme.palette.text.secondary,
    },
    icon: { minWidth: theme.spacing(5) },
  }),
  { name: "RaLogout" }
);

/**
 * Logout button component, to be passed to the Admin component
 *
 * Used for the Logout Menu item in the sidebar
 */
const LogoutWithRef: FunctionComponent<
  Props & MenuItemProps<"li", { button: true }> // HACK: https://github.com/mui-org/material-ui/issues/16245
> = React.forwardRef(function Logout(props, ref) {
  const {
    className,
    classes: classesOverride,
    redirectTo,
    icon,
    ...rest
  } = props;
  const classes = useStyles(props);
  const isXSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xs")
  );

  const translate = useTranslate();
  const logout = useLogout();
  const curUserContext = useUserContext();

  const authProvider = useOCPAuthProvider();
  const redirect = useRedirect();
  const notify = useNotify();

  const handleLogoutClick = useCallback(
    () =>
      // @ts-ignore
      logout(redirectTo, false),
    [redirectTo, logout]
  );

  const handleLoginClick = useCallback(async () => {
    authProvider
      .login()
      .then(() => {
        redirect("/");
      })
      .catch(() => {
        notify("Login failed");
      });
  }, [authProvider, notify, redirect]);

  if (curUserContext.isGuest) {
    return (
      <MenuItemLink
        className={classnames("login", classes.menuItem, className)}
        // @ts-ignore
        onClick={handleLoginClick}
        ref={ref}
        component={(isXSmall ? "span" : "li") as any}
        {...rest}
        leftIcon={icon ? icon : <LockIcon />}
        primaryText={translate("Login")}
      />
    );
  } else {
    return (
      <MenuItemLink
        className={classnames("logout", classes.menuItem, className)}
        // @ts-ignore
        onClick={handleLogoutClick}
        ref={ref}
        component={(isXSmall ? "span" : "li") as any}
        {...rest}
        leftIcon={icon ? icon : <ExitIcon />}
        primaryText={translate("ra.auth.logout")}
      />
    );
  }
});

LogoutWithRef.propTypes = {
  className: PropTypes.string,
  redirectTo: PropTypes.string,
  icon: PropTypes.element,
};

export default LogoutWithRef;
